import * as React from "react"

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { type VariantProps } from "class-variance-authority"
import { ChevronDown } from "lucide-react"

import { cn } from "../../cn"
import { buttonVariants } from "../Button/Button"
import {
  dropdownMenuContentVariants,
  DropdownMenuItem,
} from "../DropdownMenu/DropdownMenu"
import { Loader } from "../Loader/Loader"

const DIVIDER_STYLE: Record<string, React.CSSProperties> = {
  default: { backgroundColor: "rgba(255, 255, 255, 0.3)" },
  secondary: { backgroundColor: "var(--coval-border-active)" },
  ghost: { backgroundColor: "var(--coval-border)" },
  danger: { backgroundColor: "var(--coval-danger-border)", opacity: 0.3 },
}

export interface SplitButtonItem {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  disabled?: boolean
  destructive?: boolean
  "data-testid"?: string
}

export interface SplitButtonProps extends VariantProps<typeof buttonVariants> {
  children: React.ReactNode
  onClick: () => void
  items: SplitButtonItem[]
  disabled?: boolean
  /**
   * Marks the primary action as in flight: disables both segments, sets
   * `aria-busy`, and prepends a spinner to the primary label.
   */
  loading?: boolean
  className?: string
  "aria-label"?: string
  "data-testid"?: string
}

const SplitButton = React.forwardRef<HTMLButtonElement, SplitButtonProps>(
  (
    {
      children,
      onClick,
      items,
      variant,
      disabled,
      loading,
      className,
      "aria-label": ariaLabel,
      "data-testid": dataTestId,
    },
    ref,
  ) => (
    <DropdownMenuPrimitive.Root>
      <div className={cn("inline-flex", className)}>
        <button
          ref={ref}
          type="button"
          onClick={onClick}
          disabled={disabled || loading}
          aria-busy={loading || undefined}
          data-testid={dataTestId}
          className={cn(
            buttonVariants({ variant }),
            "rounded-r-none focus-visible:z-10",
          )}
        >
          {loading && <Loader size="sm" className="size-3.5 text-current" />}
          {children}
        </button>
        <div
          className="w-px self-stretch shrink-0"
          style={DIVIDER_STYLE[variant ?? "default"]}
          aria-hidden="true"
        />
        <DropdownMenuPrimitive.Trigger asChild disabled={disabled || loading}>
          <button
            type="button"
            aria-label={ariaLabel ?? "More options"}
            disabled={disabled || loading}
            data-testid={dataTestId ? `${dataTestId}-options` : undefined}
            className={cn(
              buttonVariants({ variant }),
              "rounded-l-none px-1.5 focus-visible:z-10",
            )}
          >
            <ChevronDown className="size-3.5" />
          </button>
        </DropdownMenuPrimitive.Trigger>
      </div>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={4}
          className={dropdownMenuContentVariants()}
        >
          {items.map((item, i) => (
            <DropdownMenuItem
              key={i}
              disabled={item.disabled}
              onSelect={item.onClick}
              destructive={item.destructive}
              data-testid={item["data-testid"]}
              className="[&_svg]:size-4"
            >
              <span className="flex items-center gap-2">
                {item.icon}
                {item.label}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  ),
)

SplitButton.displayName = "SplitButton"

export { SplitButton }
